import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Hero.css';
import comp from '../../Asset/images/studentcomp.jpg';
import campus from '../../Asset/images/campus.jpg';
import read from '../../Asset/images/studentread.jpg';

/* ── SVG Icons ──────────────────────────────────────────────── */
const ArrowRight = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="3" y1="10" x2="17" y2="10" />
    <polyline points="11 4 17 10 11 16" />
  </svg>
);

const ChevronLeft = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 4 7 10 13 16" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="7 4 13 10 7 16" />
  </svg>
);

/* ── Stat Icons ─────────────────────────────────────────────── */
const AwardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" />
    <polyline points="8.56 2.75 4 6 4.94 11.44" />
    <polyline points="15.44 2.75 20 6 19.06 11.44" />
    <path d="M12 14v8M9 19h6" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

/* ── Slide Data ─────────────────────────────────────────────── */
const slides = [
  {
    image: comp,
    tag: 'Academic Excellence',
    title: 'Shaping Minds,',
    titleAccent: 'Building Futures',
    subtitle: 'A rigorous, nurturing academic environment where every student is equipped to excel beyond the classroom.',
    buttonText: 'Explore Programs',
    buttonLink: '/academics',
    badge: { value: 'A+', label: 'Academic Record' },
    caption: 'Technology & Innovation Lab',
    theme: 'primary',
  },
  {
    image: campus,
    tag: 'Our Campus',
    title: 'Star Hacs',
    titleAccent: 'International School',
    subtitle: 'A modern, inspiring campus designed to nurture curiosity, creativity, and a lifelong love of learning.',
    buttonText: 'About Us',
    buttonLink: '/about',
    badge: { value: '3+', label: 'Years Strong' },
    caption: 'Our Learning Environment',
    theme: 'secondary',
  },
  {
    image: read,
    tag: 'Enrolment Open',
    title: 'Begin Your',
    titleAccent: 'Journey With Us',
    subtitle: 'Join a vibrant community of learners, educators, and families committed to excellence and growth.',
    buttonText: 'Apply Now',
    buttonLink: '/application',
    badge: { value: '98%', label: 'Satisfaction' },
    caption: 'Our Students At Work',
    theme: 'accent',
  },
];

/* ── Stats Data ─────────────────────────────────────────────── */
const stats = [
  { icon: <AwardIcon />, number: '3+',  label: 'Years of Excellence' },
  { icon: <UsersIcon />, number: '145+', label: 'Students Enrolled' },
  { icon: <HeartIcon />, number: '98%', label: 'Parent Satisfaction' },
  { icon: <BookIcon />,  number: '3',   label: 'Academic Levels' },
];

/* ── Hero Component ─────────────────────────────────────────── */
const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const total = slides.length;

  const goTo = useCallback((index) => {
    setCurrentSlide(((index % total) + total) % total);
    setProgressKey(k => k + 1);
  }, [total]);

  const next = useCallback(() => goTo(currentSlide + 1), [currentSlide, goTo]);
  const prev = useCallback(() => goTo(currentSlide - 1), [currentSlide, goTo]);

  useEffect(() => {
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next]);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <section className="hero">
      {/* Background Decorative Elements */}
      <div className="hero-bg-elements" aria-hidden="true">
        <div className="bg-arc arc-1" />
        <div className="bg-arc arc-2" />
      </div>

      <div className="hero-container">
        {/* Eyebrow */}
        <div className="hero-eyebrow">
          <span className="eyebrow-line" />
          <span className="eyebrow-text">Star Hacs International School — Accra, Ghana</span>
        </div>

        {/* Slides */}
        <div className="hero-slides" role="region" aria-label="Hero carousel">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''} ${slide.theme}`}
              aria-hidden={index !== currentSlide}
            >
              <div className="slide-content">
                {/* ── Text ── */}
                <div className="slide-text">
                  <span className="slide-tag">{slide.tag}</span>
                  <h1 className="slide-title">
                    {slide.title}{' '}
                    <span className="title-accent">{slide.titleAccent}</span>
                  </h1>
                  <p className="slide-subtitle">{slide.subtitle}</p>
                  <div className="slide-actions">
                    <Link to={slide.buttonLink} className="btn btn-primary">
                      {slide.buttonText}
                      <ArrowRight />
                    </Link>
                    <Link to="/contact" className="btn btn-ghost">
                      Contact Us
                    </Link>
                  </div>
                </div>

                {/* ── Image ── */}
                <div className="slide-image-container">
                  <div className="image-frame" aria-hidden="true" />
                  <div className="image-card">
                    <img
                      src={slide.image}
                      alt={`${slide.title} ${slide.titleAccent}`}
                      className="hero-slide-img"
                      loading={index === 0 ? 'eager' : 'lazy'}
                    />
                    <div className="image-overlay">
                      <span className="image-caption">{slide.caption}</span>
                    </div>
                    <div className="image-badge">
                      <span className="badge-value">{slide.badge.value}</span>
                      <span className="badge-label">{slide.badge.label}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Controls Row */}
          <div className="slide-controls-row">
            <span className="slide-counter">
              <span className="current">{pad(currentSlide + 1)}</span>
              &thinsp;/&thinsp;{pad(total)}
            </span>

            <div className="slide-dots" role="tablist" aria-label="Slide indicators">
              {slides.map((_, i) => (
                <button
                  key={i}
                  className={`dot ${i === currentSlide ? 'active' : ''}`}
                  onClick={() => goTo(i)}
                  role="tab"
                  aria-selected={i === currentSlide}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>

            <div className="slide-nav-btns">
              <button className="nav-btn" onClick={prev} aria-label="Previous slide">
                <ChevronLeft />
              </button>
              <button className="nav-btn" onClick={next} aria-label="Next slide">
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="slide-progress" aria-hidden="true">
          <div className="slide-progress-bar" key={progressKey} />
        </div>
      </div>

      {/* Stats Strip */}
      <div className="hero-stats" role="region" aria-label="School statistics">
        <div className="stats-inner">
          {stats.map((s, i) => (
            <div className="stat-item" key={i}>
              <div className="stat-icon" aria-hidden="true">{s.icon}</div>
              <div className="stat-text">
                <span className="stat-number">{s.number}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;